// src/utils/useRoleAuth.js
//
// Keycloak login + realm-role check for pages that need one of `roles`.
// Pages call getToken() at request time instead of holding a token: it is
// refreshed every 30 s in the background (long sessions on the map or the
// territory editor outlast Keycloak's 5-minute token), and a refresh must
// not re-run the pages' loading effects - those depend on `ready` only.
import { useCallback, useEffect, useRef, useState } from 'react';
import { initKeycloak } from '../keycloak';

export default function useRoleAuth(roles) {
  const [ready, setReady] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const kcRef = useRef(null);
  const loginRequested = useRef(false);
  const rolesKey = roles.join(',');

  useEffect(() => {
    let timer = null;
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname + window.location.search);
      if (!kc.authenticated) {
        if (loginRequested.current) return;
        loginRequested.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      if (!rolesKey.split(',').some((r) => kc.hasRealmRole(r))) setForbidden(true);
      else {
        kcRef.current = kc;
        timer = setInterval(() => { kc.updateToken(60).catch(() => kc.login()); }, 30_000);
        setReady(true);
      }
      setLoading(false);
    })().catch(console.error);
    return () => { if (timer) clearInterval(timer); };
  }, [rolesKey]);

  const getToken = useCallback(() => kcRef.current?.token ?? '', []);
  const logout = useCallback(() => kcRef.current?.logout({ redirectUri: `${window.location.origin}/` }), []);
  return { ready, forbidden, loading, getToken, logout };
}
